import type {Editor} from 'grapesjs';
import {initMergeTagsDropdown, RTE_MERGE_TAGS_LIST_ID} from '@Global/email-maker';
import {unSelectedItemsForMultipleLists} from '@Components/list/list-reducer';
import {addTagToMap} from '@Components/merge-tags/merge-tags-reducer';
import {ALIGNMENT_TYPE} from '@Components/dropdown-v2/dropdown.types';
import {isSmallScreenDropdown} from '@Components/dropdown-v2/dropdown.helper';
import {appendMergeTagToComponent, getMergeTagsFromOptionId} from '@EmailMakerReact/lib/email-maker-merge-tag-library';

/** `editor.RichTextEditor` (the RichTextEditorModule type is not exported). */
type RteManager = Editor['RichTextEditor'];

export function addMergeTagsRteAction(rte: RteManager, editor: Editor): void {
  let selectedItem = '';

  rte.add('merge-tags', {
    icon: ((): HTMLElement => {
      const dropdown = document.createElement('div');
      dropdown.setAttribute('id', RTE_MERGE_TAGS_LIST_ID);
      initMergeTagsDropdown(dropdown, {
        listId: RTE_MERGE_TAGS_LIST_ID,
        onSelect: (item) => {
          selectedItem = item;
          insertSelectedMergeTag(rte, editor, item);
        },
        alignment: ALIGNMENT_TYPE.BOTTOM_START,
      });
      return dropdown;
    })(),
    attributes: {title: window.i18next.t('pmwjs_add_merge_tag')},
    result: () => {
      const selectedComponent = editor.getSelected();

      if (!selectedItem || !selectedComponent) {
        return;
      }

      window.PMW.redux.store.dispatch(addTagToMap({ccid: selectedComponent.ccid, mergeTagKey: selectedItem}));
      window.PMW.redux.store.dispatch(unSelectedItemsForMultipleLists({listIds: [RTE_MERGE_TAGS_LIST_ID]}));
    },
  });
}

/**
 * Inserts the chosen merge-tag token into the text being edited.
 *
 * On wide screens the picker is an inline popup that keeps the canvas contenteditable focused, so
 * the live RTE session inserts the token at the caret and GrapesJS syncs it into the component
 * model. Below the threshold checked by {@link isSmallScreenDropdown} the picker opens in a modal that pulls focus
 * off the contenteditable; `insertHTML` then mutates only the DOM and the token never reaches the
 * model, so it is silently dropped on save. In that case append the token to the selected
 * component's model instead — the same reliable path the component toolbar's merge-tag button uses.
 */
function insertSelectedMergeTag(rte: RteManager, editor: Editor, mergeTagKey: string): void {
  if (!isSmallScreenDropdown()) {
    rte.globalRte?.insertHTML(getMergeTagsFromOptionId(mergeTagKey), {select: true});
    return;
  }

  const selectedComponent = editor.getSelected();

  if (!selectedComponent) {
    return;
  }

  appendMergeTagToComponent(selectedComponent, mergeTagKey, RTE_MERGE_TAGS_LIST_ID);
}
